import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, take } from 'rxjs/operators';
import { CreateRecordAction, DeleteRecordAction, DuplicateRecordAction, EditRecordAction, RecordActionTypes, OrganizeRecordAction, ViewRecordAction, AssignLoanRecordAction } from '../actions/record-actions';
import { AppStore, getAppSelection, SnackbarErrorAction, SnackbarInfoAction } from '@alfresco/aca-shared/store';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { RecordService } from '../../services/record.service';
import { UdeuService } from '../../services/udeu.service';
import { SMTConfirmDialogComponent } from 'projects/alden-extension/src/lib/dialogs/confirm/confirm.dialog';
import { Navigation, NavigationEnd, Router } from '@angular/router';
import { LoanService } from '../../services/loan.service';
import { TranslationService } from '@alfresco/adf-core';
import { MinimalNodeEntity } from '@alfresco/js-api';


@Injectable()
export class RecordEffects {
  navigation: Navigation;

  constructor(
    private store: Store<AppStore>,
    private actions$: Actions,
    private udeuService: UdeuService,
    private recordService: RecordService,
    private loanService: LoanService,
    private dialog: MatDialog,
    private router: Router,
    private translationService: TranslationService
  ) { 
    // Escuchar cambios de navegación
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)
    ).subscribe((_event: NavigationEnd) => {
      this.navigation = this.router.getCurrentNavigation();
    });
  }


  createRecord$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CreateRecordAction>(RecordActionTypes.CreateRecord),
        map((_action) => {
          this.recordService.openCreateRecordDialogComponent();
        })
      ),
    { dispatch: false }
  );

  viewRecord$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ViewRecordAction>(RecordActionTypes.ViewRecord),
        map((action) => {
          this.openRecordDialogComponent(action, RecordActionTypes.ViewRecord);
        })
      ),
    { dispatch: false }
  );

  editRecord$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<EditRecordAction>(RecordActionTypes.EditRecord),
        map((action) => {
          this.openRecordDialogComponent(action, RecordActionTypes.EditRecord);
        })
      ),
    { dispatch: false }
  );

  duplicateRecord$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<DuplicateRecordAction>(RecordActionTypes.DuplicateRecord),
        map((action) => {
          this.openRecordDialogComponent(action, RecordActionTypes.DuplicateRecord);
        })
      ),
    { dispatch: false }
  );

  deleteRecord$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<DeleteRecordAction>(RecordActionTypes.DeleteRecord),
        map((action) => {
          if (action && action.payload && action.payload.length > 0) {
            this.deleteRecord(action.payload);
          } else {
            this.store
              .select(getAppSelection)
              .pipe(take(1))
              .subscribe((selection) => {
                if (selection && selection.count > 0) {
                  this.deleteRecord(selection.nodes);
                }
              });
          }
        })
      ),
    { dispatch: false }
  );

  organizeRecord$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<OrganizeRecordAction>(RecordActionTypes.OrganizeRecord),
        map((action) => {
          this.recordService.openOrganizeRecordDialogComponent(action.payload);
      })
      ),
    { dispatch: false }
  );

  editMassiveRecord$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<EditRecordAction>(RecordActionTypes.EditMassiveRecord),
        map((action) => {
          this.openRecordDialogComponent(action, RecordActionTypes.EditMassiveRecord);
        })
      ),
    { dispatch: false }
  );

  assignLoanRecord$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<AssignLoanRecordAction>(RecordActionTypes.AssignLoanRecord),
        map((action: any) => {
          if (action && action.payload && action.payload.length > 0) {
            this.assignLoanRecord(action);
          } else {
            this.store
              .select(getAppSelection)
              .pipe(take(1))
              .subscribe((selection) => {
                if (selection && selection.count > 0) {
                  const UUIDs = selection.nodes.map((nodeEntry) => nodeEntry.entry.id);
                  this.assignLoanRecord(UUIDs);
                }
              });
          }
        })
      ),
    { dispatch: false }
  );

  private openRecordDialogComponent(action: any, recordActionType: RecordActionTypes) {
    if (action && action.payload && action.payload.length > 0) {
      this.recordService.openRecordDialogComponent(action, recordActionType);
    } else {
      this.store
        .select(getAppSelection)
        .pipe(take(1))
        .subscribe((selection) => {
          if (selection && selection.count > 0) {
            const UUIDs = selection.nodes.map((nodeEntry) => nodeEntry.entry.id);
            this.recordService.openRecordDialogComponent(UUIDs, recordActionType);
          }
        });
    }
  }

  private assignLoanRecord(UUIDs: string[]) {
    const state = this.navigation?.extras.state;
    if (state) {
      const loan = state?.loan;
      if(loan) {
        const dialogRef = this.dialog.open(SMTConfirmDialogComponent, {
          data: { 
            title: 'DEUSTO.DIALOGS.ASSIGN_LOAN.TITLE',
            htmlContent: this.translationService.instant('DEUSTO.DIALOGS.ASSIGN_LOAN.MESSAGE', { loanName: loan.name }),
            noLabel: 'DEUSTO.DIALOGS.ASSIGN_LOAN.NO_LABEL',
            yesLabel: 'DEUSTO.DIALOGS.ASSIGN_LOAN.YES_LABEL',
          },
          minWidth: '250px'
        });
    
        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.loanService.asssignRecord(loan.UUID, UUIDs).then((_res) => { 
              this.router.navigate(['viewer/loans', loan.UUID]);
            })
            .catch((err) => { 
              console.error("ERROR", err);
              this.store.dispatch(new SnackbarErrorAction('No se pudo asignar el registro al préstamo.')); 
            });
          }
        });
      }
    } else {
      const dialogRef = this.dialog.open(SMTConfirmDialogComponent, {
        data: { 
          title: 'DEUSTO.DIALOGS.ASSIGN_LOAN.TITLE',
          message: 'DEUSTO.DIALOGS.ASSIGN_LOAN.MESSAGE_2',
          noLabel: 'DEUSTO.DIALOGS.ASSIGN_LOAN.NO_LABEL',
          yesLabel: 'DEUSTO.DIALOGS.ASSIGN_LOAN.YES_LABEL',
          formField: true,
          formFieldLabel: 'DEUSTO.DIALOGS.ASSIGN_LOAN.FORM_FIELD_LABEL'
        },
        minWidth: '250px'
      });
  
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loanService.asssignRecord('', UUIDs, false, result).then((res) => { 
            this.router.navigate(['viewer/loans', res]);
          })
          .catch((err) => { 
            console.error("ERROR", err);
            this.store.dispatch(new SnackbarErrorAction('No se pudo asignar el registro al préstamo.')); 
          });
        }
      });
    }
  }
  
  private deleteRecord(items: MinimalNodeEntity[]) {
    const dialogRef = this.dialog.open(SMTConfirmDialogComponent, {
      data: {
        title: 'DEUSTO.DIALOGS.DELETE_ELEMENTS.TITLE',
        htmlContent: this.translationService.instant(items.length === 1 ? 'DEUSTO.DIALOGS.DELETE_ELEMENTS.ELEMENT_MESSAGE' : 'DEUSTO.DIALOGS.DELETE_ELEMENTS.MESSAGE', { number: items.length }),
        noLabel: 'DEUSTO.DIALOGS.DELETE_ELEMENTS.NO_LABEL',
        yesLabel: 'DEUSTO.DIALOGS.DELETE_ELEMENTS.YES_LABEL'
      },
      minWidth: '250px'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.recordService.deleteRecords(items).then(() => {
          this.udeuService.reloadCustomListTable();
          this.store.dispatch(new SnackbarInfoAction(items.length === 1 ? 'Registro eliminado correctamente.' : 'Registros eliminados correctamente.'));
        }).catch((err) => {
          console.error("ERROR", err);
          this.store.dispatch(new SnackbarErrorAction(items.length === 1 ? 'No se pudo eliminar el registro.' : 'No se pudo eliminar los registros.'));
        })
      }
    });
  }
}
